// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Implement membrane product.
 * @author James Reid
 */

// @ts-check

// @@imports-module
import { Product } from "./Product.js"

// @@imports-types
import {
    ProductData,
    MembraneType,
    UNITS,
    MEMBRANE_TYPES,
    PRODUCT_DISPLAY_CATEGORIES
} from "../types/index.js"

// @@body
class Membrane extends Product {
    /**
     * Construct membrane product from data returned from server.
     * @param {ProductData} data Product data returned from server.
     * @param {any} match Regexp match data for internal calculator sku code
     * pertaining to this extended class.
     */
    constructor(data, match) {
        super(data, {
            minimumFraction: 0.1,
            unit: UNITS.length.metre,
            isUniqueLineItem: false
        })

        // Parse regexp data.
        const { type, thickness, width } =
            /** @type {Object<string, string>} */ (match.groups)

        // Set extended class values.
        /** @type {MembraneType} */
        this.type = type === "S" ? MEMBRANE_TYPES.onePiece
            : MEMBRANE_TYPES.fleeceBacked
        this.displayCategory = PRODUCT_DISPLAY_CATEGORIES.membrane

        // Infer exact dimensions required for calculations from calculator sku.
        this.thickness = parseInt(thickness) / 100000
        this.width = parseInt(width) / 100
    }
}

// @@exports
export { Membrane }
